import React from 'react';
import styled from 'styled-components';
import { background, border, color, compose, flexbox, grid, layout, position, shadow, space, typography } from 'styled-system';
import { CircularProgress, colors, pallette, Typography } from '@equinor/opt-ui-core';
import { ErrorOutlined } from '@equinor/opt-ui-icons';
import { colors as themeColors, zIndex } from 'AppTheme/constants';
// Styled components
export const Container = styled.div `
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
`;
export const Block = styled.div `
  position: relative;
  ${({ columnGap }) => columnGap && `column-gap: ${columnGap}`};
  ${compose(space, color, typography, layout, flexbox, grid, background, border, position, shadow)};
`;
export const GridBlock = styled.div `
  position: relative;
  display: grid;
  ${({ gridTemplate }) => gridTemplate && `grid-template: ${gridTemplate}`};
  ${({ columnGap }) => columnGap && `column-gap: ${columnGap}`};
  ${compose(space, color, typography, layout, flexbox, grid, background, border, position, shadow)};
`;
export const FlexBlock = styled.div `
  position: relative;
  display: flex;
  background-color: transparent;
  ${({ columnGap }) => columnGap && `column-gap: ${columnGap}`};
  ${compose(space, color, typography, layout, flexbox, grid, background, border, position, shadow)}
`;
// Panel
export const PanelContainer = styled.div `
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: ${(props) => props?.backgroundColor};
  min-width: ${(props) => `${props?.minWidth}px`};
`;
export const PanelControlsContainer = styled(FlexBlock) `
  padding: 0px 16px !important;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 4px;
  background-color: ${(props) => pallette(props).background.paper};

  @media only screen and (max-width: 500px) {
    padding: 0px;
  }
`;
export const PanelTopBar = styled.div `
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 2px 10px 0 10px;
  height: 48px;

  @media only screen and (max-width: 500px) {
    height: 100px;
  }
`;
export const PanelTopBarRight = styled(FlexBlock) `
  z-index: ${zIndex.controls};
  align-items: center;
`;
export const PanelTopBarRightButtonsContainer = styled(FlexBlock) `
  .opt-button {
    width: 32px;
    min-width: 32px;
    height: 32px;
    min-height: 32px;
  }

  @media only screen and (max-width: 500px) {
    flex-direction: column-reverse;
    flex-wrap: wrap;
    justify-content: space-around;
  }
`;
export const PanelContent = styled.div `
  padding: 0px 8px;
  display: flex;
  width: 100%;
  height: 100%;
`;
export const PanelMainView = styled.div `
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  > .opt-buttonGroup {
    .opt-button {
      > span {
        color: ${colors.primary};
      }
    }
  }
`;
export const LoaderWrapper = styled.div `
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: ${({ height }) => height ?? '100%'};
  width: 100%;
`;
export const MessageStateContainer = styled(FlexBlock) `
  width: 100%;
  height: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: ${(props) => pallette(props)?.text.main};
  padding: 1.5rem;
`;
export const PageContainer = styled(Container) `
  flex-direction: column;
`;
export const TopPaneContainer = styled(Container) `
  margin-top: 0;
  border-color: #000000;
  border-style: solid;
  border-width: 2px;
  border-top-width: 1px;
  border-bottom-width: 1px;

  > div {
    border-color: #000000;
    border-style: solid;
  }

  @media only screen and (max-width: 767px) {
    flex-direction: column;
    height: 90%;
    max-height: 30svh;

    > div {
      border-top-width: 1px;
      border-bottom-width: 1px;
      border-left-width: 0;
      border-right-width: 0;
    }

    > div:nth-child(1) {
      border-top-width: 0;
      border-bottom-width: 1px;
    }

    > div:nth-last-child(1) {
      border-top-width: 1px;
      border-bottom-width: 0;
    }
  }

  @media only screen and (min-width: 768px) {
    flex-direction: row;
    min-height: 380px;
    max-height: 45%;

    > div {
      width: ${({ fullWidth = true }) => (fullWidth ? '100%' : '50%')};
      height: 100%;
      overflow: auto;
      border-top-width: 0;
      border-bottom-width: 0;
      border-left-width: 0;
      border-right-width: 2px;
    }

    > div:last-child {
      border-right-width: 0;
    }
  }
`;
export const BottomPaneContainer = styled(Container) `
  margin-top: 0;
  margin-right: 10px;
  border-color: #000000;
  border-style: solid;
  border-width: 2px;
  border-top-width: 1px;
  border-bottom-width: 0;
  height: 100%;
  flex-direction: column;
  overflow: auto;

  @media only screen and (max-width: 767px) {
    max-height: 50svh;
  }
`;
export const ControlsContainer = styled.div `
  border-style: solid;
  border-color: #000000;
  border-top-width: 2px;
  border-bottom-width: 1px;
  border-left: 0;
  border-right: 0;
  background-color: ${themeColors.SLATE_BLUE_100};
  overflow-x: auto;
  overflow-y: hidden;
  min-height: 38px;
  @media only screen and (max-width: 767px) {
    min-height: 40px;
  }
`;
export const BlockBorder = styled.div `
  background-color: #000000;
  width: 2px;
  height: 100%;
  margin: 0 5px;
`;
export const PanelWrapper = ({ className = '', backgroundColor = 'transparent', minWidth = 0, children }) => (React.createElement(PanelContainer, { className: className, backgroundColor: backgroundColor, minWidth: minWidth },
    React.createElement(PanelContent, null,
        React.createElement(PanelMainView, null, children))));
export const ErrorState = ({ text = 'Error loading data', textColor = undefined }) => (React.createElement(MessageStateContainer, null,
    React.createElement(ErrorOutlined, { color: textColor }),
    React.createElement(Typography, { padding: "0.5rem", textAlign: "center", color: textColor }, text)));
export const EmptyState = ({ text = 'No results' }) => (React.createElement(MessageStateContainer, null,
    React.createElement(Typography, null, text)));
export const LoadingState = ({ text = '', height = '100%' }) => (React.createElement(LoaderWrapper, { height: height },
    React.createElement(CircularProgress, null),
    text && React.createElement(Typography, null, text)));
export const TagLoadingError = (props) => {
    const { label } = props;
    return (React.createElement(FlexBlock, { marginTop: "4px", marginLeft: "20px", alignItems: "center" },
        React.createElement(ErrorOutlined, { color: colors.danger }),
        React.createElement(Typography, { ml: "5px", fontSize: "0.9rem" }, label)));
};
export const InputDescription = styled.div `
  position: relative;
  display: flex;
  width: 100%;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.333em;
  padding-top: 8px;
  padding-left: 8px;
  > span {
    flex: 1;
  }
  color: ${(props) => pallette(props).text?.tertiary || colors.grey50};
  &.opt-input__description--success {
    color: ${(props) => pallette(props).success?.main || colors.success};
    &.has-focus {
      color: ${(props) => pallette(props).success?.hover || colors.successHover};
    }
  }
  &.opt-input__description--warning {
    color: ${(props) => pallette(props).warning?.main || colors.warning};
    &.has-focus {
      color: ${(props) => pallette(props).warning?.hover || colors.warningHover};
    }
  }
  &.opt-input__description--error {
    color: ${(props) => pallette(props).danger?.main || colors.danger};
    &.has-focus {
      color: ${(props) => pallette(props).danger?.hover || colors.dangerHover};
    }
  }
  &[aria-disabled='true'] {
    color: ${(props) => pallette(props).disabled?.text || colors.disabledText} !important;
  }
`;
export const TagChartPanel = styled.div `
  height: 500px;
  z-index: 10000;
`;
